import { XIcon, CodeXmlIcon } from "lucide-react";
import { useValidateCustomFormLazyQuery } from "@/apollo/types";
import { Alert, AlertIcon } from "@/components/elements/Alert";
import { Button, IconButton } from "@/components/elements/Button";
import { Input } from "@/components/primitives/input";
import FieldLabel from "@/components/primitives/InputLabel";
import { TextArea } from "@/components/primitives/input/TextArea";
import { useCallback, useState } from "react";

import { ShopifyReport } from "./ShopifyForm";
import { snakeCase } from "lodash";

export default function ShopifyCustomReport(props: {
  onChangeReport: (report: ShopifyReport) => void;
  onRemoveReport: (report: ShopifyReport) => void;
  onReportInvalid: (reportId: string) => void;
  onReportValid: (reportId: string) => void;
  report: ShopifyReport;
  connectionId: string;
  validationError?: string;
}) {
  const {
    report,
    onChangeReport,
    onRemoveReport,
    validationError,
    connectionId,
    onReportValid,
    onReportInvalid,
  } = props;

  const [validationSuccess, setValidationSuccess] = useState<boolean>(false);
  const [validationCallErrors, setValidationCallErrors] = useState<
    string[] | null
  >(null);

  const [callValidationQuery, { loading: validationQueryLoading }] =
    useValidateCustomFormLazyQuery();

  const runValidation = useCallback(async () => {
    const queryData = {
      query: report.query,
      id: report.id,
    };
    const { data, error } = await callValidationQuery({
      variables: {
        input: {
          connectionId,
          payload: JSON.stringify({
            report: queryData,
          }),
        },
      },
    });

    if (error) {
      setValidationSuccess(false);
      setValidationCallErrors([error.message]);
      onReportInvalid(report.id);
    }
    const result = data?.validateCustomForm.customFormValidationResult;
    if (result) {
      if (result.valid) {
        setValidationSuccess(true);
        onReportValid(report.id);
        setValidationCallErrors(null);

        const columns = result.columns.map(
          (col: { name: string; dataType: string; displayName: string }) => ({
            name: col.name,
            dataType: col.dataType,
          }),
        );
        onChangeReport({ ...report, columns, valid: true });
      } else {
        setValidationSuccess(false);
        setValidationCallErrors(result.parseErrors);
        onReportInvalid(report.id);
        onChangeReport({ ...report, columns: [], valid: false });
      }
    }
  }, [
    callValidationQuery,
    connectionId,
    onReportInvalid,
    onReportValid,
    onChangeReport,
    report,
  ]);

  const changeReport = (report: ShopifyReport) => {
    onChangeReport(report);
    setValidationSuccess(false);
  };

  return (
    <div className="relative rounded-xs border bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700">
      <div className="pb-4">
        <FieldLabel htmlFor={`${report.id}-name`} required={true}>
          Name
        </FieldLabel>
        <Input
          id={`${report.id}-name`}
          placeholder="Name of your report"
          value={report.name}
          onChange={(e) => changeReport({ ...report, name: e.target.value })}
        />
      </div>
      <div className="pb-4">
        <FieldLabel htmlFor={`${report.id}-query`} required={true}>
          ShopifyQL Query
        </FieldLabel>
        <TextArea
          id={`${report.id}-query`}
          placeholder={`Enter your ShopifyQL query here, e.g.:\nFROM sales\n  SHOW total_sales\n  GROUP BY product_title`}
          className="h-48 font-mono text-sm"
          value={report.query}
          onChange={(e) => changeReport({ ...report, query: e.target.value })}
        />
      </div>
      <div className="flex justify-between">
        <Button
          size="sm"
          variant="solid"
          colorScheme="primary"
          isLoading={validationQueryLoading}
          loadingText="Validating..."
          onClick={() => runValidation()}
        >
          Validate report
        </Button>
        <Button
          size="sm"
          variant="solid"
          colorScheme="secondary"
          icon={<CodeXmlIcon />}
          as="a"
          href="https://shopify.dev/docs/api/shopifyql"
          target="_blank"
          rel="noreferrer"
        >
          ShopifyQL docs
        </Button>
      </div>
      <div className="absolute top-0 right-0 p-2">
        <IconButton
          size="xs"
          icon={<XIcon />}
          variant="ghost"
          aria-label="Remove report"
          onClick={() => onRemoveReport(report)}
        />
      </div>
      <div className="mt-4">
        <span className="text-sm font-bold">Validation result:</span>
        {validationSuccess ? (
          <div className="mt-2">
            <Alert status="success">
              <AlertIcon />
              <div className="flex flex-col">
                Report is valid.
                <span className="text-sm">
                  Table name: <i>{snakeCase(report.name)}</i>
                </span>
              </div>
            </Alert>
          </div>
        ) : null}
        {validationError ? (
          <div className="mt-2">
            <Alert status="error">
              <AlertIcon />
              {validationError}
            </Alert>
          </div>
        ) : null}
        {!validationCallErrors && !validationSuccess ? (
          <div className="mt-2">
            <Alert status="info">
              <AlertIcon />
              Validate your report to continue
            </Alert>
          </div>
        ) : null}
        {validationCallErrors ? (
          <div className="mt-2">
            <Alert status="error">
              <AlertIcon />
              <div className="flex flex-col">
                {validationCallErrors.map((e, i) => (
                  <span key={i}>
                    Error {i + 1}: {e}
                  </span>
                ))}
              </div>
            </Alert>
          </div>
        ) : null}
        {report.columns && report.columns.length > 0 && (
          <ShopifyQlColumns columns={report.columns} />
        )}
      </div>
    </div>
  );
}

function ShopifyQlColumns({
  columns,
}: {
  columns: { name: string; dataType: string }[];
}) {
  return (
    <div className="mt-4">
      <span className="text-sm font-medium">
        Report produces the following {columns.length}{" "}
        {columns.length === 1 ? "column" : "columns"}:
      </span>
      <div className="mt-2 flex max-h-60 flex-col gap-2 overflow-y-auto pr-4">
        {[...columns].map((col, index) => (
          <div
            key={col.name + index}
            className="flex items-center justify-between rounded-xs bg-gray-50 dark:bg-gray-700"
          >
            <span className="text-sm">{col.name}</span>
            <span className="text-sm font-semibold">{col.dataType}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
